import {
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
  IonCard,
  IonButtons,
  IonFooter,
  IonToolbar,
  useIonToast,
  IonLoading,
  IonToggle,
} from "@ionic/react";

import {useMemo, useState} from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Category } from "../../../@types/Category";
import InputErrorMessage from "../../../components/InputErrorMessage";
import { createFileType } from "../../../store/fileTypes/actions/createFileType";
import { findFileTypes } from "../../../store/fileTypes/actions/findFileTypes";
import { selectCreateFileTypeStatus } from "../../../store/fileTypes/selectors/selectCreateFileTypeStatus";

const initialFileType = {
  name: "",
  module: "document",
  isSignable: true,
  htmlDescription: "",
  datesRequire: true,
  expirationDays: 0,
  enabled: true,
  requireCode: true,
  sendMessageWhatsApp: true,
  sendMessageSMS: true,
} as Category;

export const FileTypeCreateForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const createFileTypeStatus = useSelector(selectCreateFileTypeStatus);
  const [presentToast] = useIonToast();

  const [fileType, setFileType] = useState<Category>(initialFileType);

  const handleLoadingFinish = () => {
    if (createFileTypeStatus === "succeeded") {
      presentToast({
        message: "Tipo de archivo creado correctamente",
        duration: 3000,
        color: "success",
      });
      dispatch(findFileTypes());
      history.replace("/app/tic/tipos-de-archivos/");
    }
    if (createFileTypeStatus === "failed") {
      presentToast({
        message: "Ocurrió un error al tratar de crear el tipo de archivo",
        duration: 3000,
        color: "danger",
      });
    }
  };

  const handleOnChange = (type: string, checked: boolean) => {
    switch (type) {
      case "isSignable":
        setFileType({ ...fileType, isSignable: checked });
        break;
      case "datesRequire":
        setFileType({ ...fileType, datesRequire: checked });
        break;
      case "enabled":
        setFileType({ ...fileType, enabled: checked });
        break;
      case "requireCode":
        setFileType({ ...fileType, requireCode: checked });
        break;
      case "sendMessageWhatsApp":
        setFileType({ ...fileType, sendMessageWhatsApp: checked });
        break;
      case "sendMessageSMS":
        setFileType({ ...fileType, sendMessageSMS: checked });
        break;
      default:
        break;
    }
  };

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      name: "",
      expirationDays: 0,
      htmlDescription: "",
    },
  });

  const onSubmit = async (data: any) => {
    const finalFileType = {
      ...fileType,
      name: data.name,
      expirationDays: data.expirationDays,
      htmlDescription: data.htmlDescription,
    } as Category;
    dispatch(createFileType(finalFileType));
  };

  const controllers = useMemo(()=>{
    return [({ field: { onChange, value } }:any) => (
      <IonInput
        value={value}
        placeholder="Ingrese el nombre del tipo de archivo"
        onIonChange={({ detail: { value } }) => onChange(value)}
        {...register("name", {
          required: "Campo obligatorio.",
        })}
      />
    ),({ field: { onChange, value } }:any) => (
      <IonInput
        value={value}
        type="number"
        placeholder="Ingrese cantidad de días"
        onIonChange={({ detail: { value } }) => onChange(value)}
        {...register("expirationDays", {
          valueAsNumber: true,
        })}
      />
    ),({ field: { onChange, value } }:any) => (
      <IonInput
        value={value}
        placeholder="Ingrese descripción"
        onIonChange={({ detail: { value } }) => onChange(value)}
        {...register("htmlDescription", {
          // required: "Campo obligatorio.",
        })}
      />
    )]
  },[])

  return (
    <>
      <form noValidate onSubmit={handleSubmit(onSubmit)} style={{ padding: 18 }}>
        <IonCard style={{ maxWidth: 700 }}>
          <section className="ion-padding">
            <IonItem lines="inset">
              <IonLabel position="stacked">Nombre</IonLabel>
              <Controller
                name="name"
                control={control}
                render={controllers[0]}
              />
            </IonItem>
            <InputErrorMessage errorMessage={errors.name?.message || ""} paddingLeft={15} />

            <IonItem>
              <IonLabel>Firmable</IonLabel>
              <IonToggle
                checked={fileType.isSignable}
                onIonChange={({ detail: { checked } }) => handleOnChange("isSignable", checked)}
              />
            </IonItem>

            <IonItem>
              <IonLabel>Requiere fecha de vencimiento</IonLabel>
              <IonToggle
                checked={fileType.datesRequire}
                onIonChange={({ detail: { checked } }) => handleOnChange("datesRequire", checked)}
              />
            </IonItem>

            <IonItem lines="inset">
              <IonLabel position="stacked">Aviso de días para vencer</IonLabel>
              <Controller
                name="expirationDays"
                control={control}
                render={controllers[1]}
              />
            </IonItem>
            <InputErrorMessage errorMessage={errors.expirationDays?.message || ""} />

            <IonItem lines="inset">
              <IonLabel position="stacked">Descripción</IonLabel>
              <Controller
                name="htmlDescription"
                control={control}
                render={controllers[2]}
              />
            </IonItem>
            <InputErrorMessage
              errorMessage={errors.htmlDescription?.message || ""}
              paddingLeft={15}
            />

            <IonItem>
              <IonLabel>Activo</IonLabel>
              <IonToggle
                checked={fileType.enabled}
                onIonChange={({ detail: { checked } }) => handleOnChange("enabled", checked)}
              />
            </IonItem>

            <IonItem>
              <IonLabel>Requiere ficha</IonLabel>
              <IonToggle
                checked={fileType.requireCode}
                onIonChange={({ detail: { checked } }) => handleOnChange("requireCode", checked)}
              />
            </IonItem>

            <IonItem>
              <IonLabel>Notificación automática vía WhatsApp</IonLabel>
              <IonToggle
                checked={fileType.sendMessageWhatsApp}
                onIonChange={({ detail: { checked } }) =>
                  handleOnChange("sendMessageWhatsApp", checked)
                }
              />
            </IonItem>

            <IonItem>
              <IonLabel>Notificación automática vía SMS</IonLabel>
              <IonToggle
                checked={fileType.sendMessageSMS}
                onIonChange={({ detail: { checked } }) => handleOnChange("sendMessageSMS", checked)}
              />
            </IonItem>

            <br />
          </section>

          <IonFooter>
            <IonToolbar color="light">
              <IonButtons slot="end">
                <IonButton fill="solid" type="submit">
                  Crear tipo de archivo
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonFooter>
        </IonCard>

        <IonLoading
          isOpen={createFileTypeStatus === "pending"}
          message={"Creando tipo de archivo..."}
          onDidDismiss={handleLoadingFinish}
        />
      </form>
    </>
  );
};
